import React, { useEffect, useRef } from "react";

// Living neural-network background. Nodes drift, connect when close, and ignite
// toward the cursor with the flame accent.
export const NeuralBackground = ({ className = "", density = 0.00009 }) => {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: -9999, y: -9999 });
  const raf = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let nodes = [];
    let w = 0, h = 0, dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.min(110, Math.max(34, Math.floor(w * h * density)));
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.28,
        vy: (Math.random() - 0.5) * 0.28,
      }));
    };

    const LINK = 150;
    const PULL = 170;

    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      const mx = mouse.current.x, my = mouse.current.y;

      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;

        const dmx = n.x - mx, dmy = n.y - my;
        const dm = Math.hypot(dmx, dmy);
        if (dm < PULL) {
          const f = (1 - dm / PULL) * 0.6;
          n.x -= (dmx / (dm || 1)) * f;
          n.y -= (dmy / (dm || 1)) * f;
        }
      }

      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        const aNear = Math.hypot(a.x - mx, a.y - my) < PULL;
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d < LINK) {
            const near = aNear || Math.hypot(b.x - mx, b.y - my) < PULL;
            const o = (1 - d / LINK) * (near ? 0.85 : 0.16);
            ctx.strokeStyle = near ? `rgba(255,106,0,${o})` : `rgba(140,140,150,${o})`;
            ctx.lineWidth = near ? 1 : 0.6;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      for (const n of nodes) {
        const near = Math.hypot(n.x - mx, n.y - my) < PULL;
        ctx.beginPath();
        ctx.arc(n.x, n.y, near ? 2.4 : 1.5, 0, Math.PI * 2);
        ctx.fillStyle = near ? "#FF6A00" : "rgba(190,190,200,0.55)";
        ctx.fill();
      }
    };

    let running = false;
    const stop = () => { running = false; cancelAnimationFrame(raf.current); };
    const loop = () => { tick(); if (running) raf.current = requestAnimationFrame(loop); };
    const startLoop = () => { if (!running && !reduce) { running = true; raf.current = requestAnimationFrame(loop); } };

    resize();
    // Only animate while the canvas is on-screen to avoid starving the main thread.
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) startLoop();
      else stop();
    }, { threshold: 0.01 });
    io.observe(canvas);
    if (reduce) tick();

    window.addEventListener("resize", resize);
    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      mouse.current = { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const onLeave = () => { mouse.current = { x: -9999, y: -9999 }; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseleave", onLeave);

    return () => {
      stop();
      io.disconnect();
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
    };
  }, [density]);

  return <canvas ref={canvasRef} className={className} style={{ width: "100%", height: "100%" }} />;
};
